import React, { useState, useCallback, useMemo } from "react";
import { TableVirtuoso } from "react-virtuoso";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  Tooltip,
  Chip,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import style from "./table.module.css";
import moment from "moment";
import { SimCard } from "@mui/icons-material";

const getColor = (value, clickable) => {
  const colorMap = {
    active: "green",
    inactive: "#dade17",
    approved: "green",
    accepted:"green",
    completed: "green",
    rejected: "red",
    success: "green",
    successful: "green",
    pending: "#d2d200",
    inprogress: "#d2d200",
    created: "grey",
    available: "green",
    activated: "blue",
    retired: "orange",
    terminated: "red",
    suspended: "red",
    Failed: "red",
    Compliant: "green",
    
  };

  const val =
    typeof value === "string" && value.trim().length > 0
      ? value.toLowerCase()
      : "";

  return colorMap[val] || (clickable ? "#418fe1" : "black");
};

const getStatusColor = (status) => {
  switch (status?.toUpperCase?.()) {
    case "CREATED":
      return "rgb(225 225 225)";
    case "APPROVED":
      return "#95e198";
    case "REJECTED":
      return "#da857f";
    // case "Pending":
    //   return "#95e198";
    default:
      return "";
  }
};

const sortDataByDate = (data) => {
  if (!data || data.length === 0) return data;
  
  const dataCopy = [...data];
  const sampleItem = dataCopy[0];
  const dateField = 
    'createdAt' in sampleItem ? 'createdAt' : 
    'submittedAt' in sampleItem ? 'submittedAt' :
    'timestamp' in sampleItem ? 'timestamp' :
    'date' in sampleItem ? 'date' : null;
    
  if (!dateField) return dataCopy;
  
  return dataCopy.sort((a, b) => {
    const valA = a[dateField] || "";
    const valB = b[dateField] || "";
    return String(valB).localeCompare(String(valA), undefined, { numeric: true });
  });
};

const TableWrapper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(2),
  overflow: "hidden",
}));

// Simplified VirtuosoTableComponents - no custom TableRow to avoid conflicts
const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer {...props} ref={ref} sx={{
      maxHeight: "37.5rem",
      padding: "0px 4px",
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        borderRadius: "10px",
        border: "2px solid transparent",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#f1f1f1",
        borderRadius: "10px",
      },
      scrollbarWidth: "thin",
      scrollbarColor: "#888 #f1f1f1",
    }} />
  )),
  Table: (props) => <Table {...props} sx={{ minWidth: 300, borderCollapse: 'separate', borderSpacing: '0 10px' }} aria-label="common-table" />,
  TableHead: React.forwardRef((props, ref) => (
    <TableHead {...props} ref={ref} sx={{
      position: "sticky",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 1,
    }} />
  )),
  TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

const SimManagementTable = ({
  columns,
  data,
  setData,
  filters,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  tableCss,
  handleGetById,
  iscard,
  onRowClick,
  showsort,
}) => {
  const serialColumnIndex = columns.findIndex((col) => col.key === "serial");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [hasManualSort, setHasManualSort] = useState(false);

  const handleSort = useCallback((key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedData = [...data].sort((a, b) => {
      const valA = a[key] || "";
      const valB = b[key] || "";

      return direction === "asc"
        ? String(valA).localeCompare(String(valB), undefined, { numeric: true })
        : String(valB).localeCompare(String(valA), undefined, {
            numeric: true,
          });
    });

    setData(sortedData);
    setSortConfig({ key, direction });
    setHasManualSort(true); 
  }, [sortConfig, data, setData]);

  // Use the original non-functional update for instant response, as in your fixed version
  const handleIndividualCheckboxChange = useCallback((iccid, checked) => {
    const updated = data.map(item => 
      item.iccid === iccid 
        ? { ...item, checked } 
        : item
    );
    setData(updated);
  }, [data, setData]);

  const handleSelectAllChange = useCallback((checked) => {
    const updated = data.map(item => ({
      ...item,
      checked
    }));
    setData(updated);
  }, [data, setData]);
  
  const allChecked = useMemo(() => 
    data?.length > 0 && data?.every(item => item?.checked), 
    [data]
  );
  
  const someChecked = useMemo(() => 
    data?.some(item => item?.checked) && !allChecked, 
    [data, allChecked]
  );

  const displayData = useMemo(() => {
    if (hasManualSort) {
      return data;
    }
    return sortDataByDate(data);
  }, [data, hasManualSort]);
  
  const visibleColumns = useMemo(() => 
    columns.filter(col => col.key !== "selectAll" && col?.showTable),
    [columns]
  );

  const nonSerialColumns = useMemo(() => 
    visibleColumns.filter(col => col.key !== "serial"),
    [visibleColumns]
  );

  const totalCols = useMemo(() => {
    const serialCount = serialColumnIndex !== -1 ? 1 : 0;
    return serialCount + nonSerialColumns.length;
  }, [serialColumnIndex, nonSerialColumns.length]);

  const fixedHeaderContent = useCallback(() => (
    <TableRow className={style.commonStylingForHeader}>
      {serialColumnIndex !== -1 && (
        <TableCell
          align="center"
          style={{
            borderRadius: "14px 0px 0px 14px",
            borderBottom: "none",
            color: "white",
            minWidth: 90,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {columns[serialColumnIndex]?.showcheck && (
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
              sx={{ color: "white", "&.Mui-checked": { color: "white" }, "&.MuiCheckbox-indeterminate": { color: "white" } }}
            />
          )}
          Sr. No
        </TableCell>
      )}

      {nonSerialColumns.map(
        (column, index) => (
          <TableCell
            key={column.key}
            align={column.align || "left"}
            style={{
              color: "white",
              minWidth:
        column.key === "masterRequestId"
          ? 190
          : column.minWidth || 100,
              borderBottom: "none",
              borderRadius:
                index === nonSerialColumns.length - 1
                  ? "0px 14px 14px 0px"
                  : undefined,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              {column.name}
              {showsort && (
                <span
                  onClick={() => handleSort(column.key)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    userSelect: "none",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    background:
                      sortConfig.key === column.key
                        ? "rgba(0, 0, 0, 0.08)"
                        : "transparent",
                    color: "inherit",
                    fontWeight:
                      sortConfig.key === column.key ? "600" : "500",
                    fontSize: "14px",
                    boxShadow:
                      sortConfig.key === column.key
                        ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                        : "none",
                    transform: "translateY(0)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      sortConfig.key === column.key
                        ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                        : "0 2px 8px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      sortConfig.key === column.key
                        ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                        : "none";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "4px",
                      borderRadius: "50%",
                      background: "rgba(0, 0, 0, 0.05)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {sortConfig.key === column.key ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpwardIcon
                          style={{
                            fontSize: "16px",
                            filter:
                              "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                          }}
                        />
                      ) : (
                        <ArrowDownwardIcon
                          style={{
                            fontSize: "16px",
                            filter:
                              "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                          }}
                        />
                      )
                    ) : (
                      <ArrowUpwardIcon
                        style={{
                          fontSize: "16px",
                          opacity: 0.6,
                          filter:
                            "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                        }}
                      />
                    )}
                  </div>
                </span>
              )}
            </div>
          </TableCell>
        )
      )}
    </TableRow>
  ), [serialColumnIndex, columns, nonSerialColumns, allChecked, someChecked, showsort, sortConfig, handleSelectAllChange, handleSort]);

  const rowContent = useCallback((index, row) => {
    const displayIndex = index;
    
    // Handle row click
    const handleRowClick = (e) => {
      // Don't trigger row click if clicking on checkbox or clickable column
      if (!e.target.closest('.MuiCheckbox-root') && 
          !e.target.closest('[data-clickable="true"]') &&
          iscard && 
          typeof onRowClick === "function") {
        onRowClick(row);
      }
    };

    return (
      <React.Fragment>
        {serialColumnIndex !== -1 && (
          <TableCell
            align="center"
            sx={{
              borderRadius: "14px 0px 0px 14px",
              borderBottom: "none",
              padding: tableCss?.cellPadding || "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {columns[serialColumnIndex]?.showcheck && (
              <Checkbox
                checked={row?.checked || false}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  handleIndividualCheckboxChange(row.iccid, e.target.checked);
                }}
              />
            )}
            {filters?.pageNo
              ? filters?.pageNo * filters?.pageSize + displayIndex + 1
              : displayIndex + 1}
          </TableCell>
        )}

        {nonSerialColumns.map(
          (column, colIndex) => (
            <TableCell
              key={column.key}
              align={column.align || "left"}
              data-clickable={column.clickable ? "true" : "false"}
              sx={{
                cursor: column.clickable ? "pointer" : "default",
                borderBottom: "none",
                padding: tableCss?.cellPadding || "12px 16px",
                borderRadius:
                  colIndex === nonSerialColumns.length - 1
                    ? "0px 14px 14px 0px"
                    : undefined,
                color: getColor(
                  row[column.key],
                  column.clickable
                ),
                visibility:
                  row.status === "SUCCESSFUL" &&
                  column.key === "action"
                    ? "hidden"
                    : "visible",
              }}
              onClick={(e) => {
                if (
                  column.clickable &&
                  typeof handleGetById === "function"
                ) {
                  e.stopPropagation(); // Prevent row click when clicking on clickable column
                  handleGetById(row, displayIndex);
                }
              }}
            >
              {Array.isArray(row[column.key]) ? (
                column.key === "allocatedTos" ? (
                  <Tooltip title={row[column.key].join(", ")}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {row[column.key]
                        .slice(0, 2)
                        .map((account, idx) => (
                          <Chip
                            key={idx}
                            label={account}
                            size="small"
                            color="primary"
                          />
                        ))}
                      {row[column.key].length > 2 && (
                        <Chip
                          label={`+${row[column.key].length - 2}`}
                          size="small"
                        />
                      )}
                    </Box>
                  </Tooltip>
                ) : (
                  <>
                    {row[column.key][0]}
                    {row[column.key].length > 1 && (
                      <span> +{row[column.key].length - 1}</span>
                    )}
                  </>
                )
              ) : column?.date ? (
                row[column.key] ? (
                  moment(row[column.key]).format("DD-MM-YYYY")
                ) : (
                  "N/A"
                )
              ) : (
                row[column.key] === 0 ? "0" : (row[column.key] || "-")
              )}
            </TableCell>
          )
        )}
      </React.Fragment>
    );
  }, [nonSerialColumns, serialColumnIndex, columns, tableCss, filters, handleIndividualCheckboxChange, handleGetById, iscard, onRowClick]);
  const CustomTableRow = useCallback(({ children, style, ...props }) => {
    const index = props['data-index'];
    const row = displayData[index];
    if (!row) return <TableRow {...props} style={style}>{children}</TableRow>;

    const handleRowClick = (e) => {
      if (!e.target.closest('.MuiCheckbox-root') && 
          !e.target.closest('[data-clickable="true"]') &&
          iscard && 
          typeof onRowClick === "function") {
        onRowClick(row);
      }
    };

    return (
      <TableRow
        {...props}
        style={style}
        hover
        role="checkbox"
        tabIndex={-1}
        sx={{
          background: getStatusColor(row?.kycStatus),
          borderRadius: "14px",
          boxShadow: "0px 0px 3px 0px grey",
          cursor: iscard ? "pointer" : "default",
          "&:hover": {
            boxShadow: "0px 0px 1px 2px #709ed3",
            backgroundColor: iscard ? "#c5d9f1" : "#f9f9f9",
          },
        }}
        onClick={handleRowClick}
      >
        {children}
      </TableRow>
    );
  }, [displayData, iscard, onRowClick, getStatusColor]);

  const virtuosoComponents = useMemo(() => ({
    ...VirtuosoTableComponents,
    TableRow: CustomTableRow,
  }), [CustomTableRow]);

  return (
    <TableWrapper
      className={style.commonTableContainer}
      sx={{ width: tableCss?.tableWidth || "95%!important" }}
    >
      {displayData?.length > 0 ? (
        <TableVirtuoso
          data={displayData}
          components={virtuosoComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={rowContent}
          style={{ height: "37.5rem" }}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          overscan={5}
        />
      ) : (
        <TableContainer sx={{ padding: "0px 4px" }}>
          <Table sx={{ minWidth: 300, borderCollapse: 'separate', borderSpacing: '0 10px' }}>
            <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
              {fixedHeaderContent()}
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={totalCols}
                  sx={{
                    borderRadius: "14px",
                    boxShadow: "0px 0px 3px 0px grey",
                    borderBottom: "0px solid rgba(224, 224, 224, 1)!important",
                  }}
                >
                  No Data To Show!
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {totalRows > 10 && (
        <TablePagination
  rowsPerPageOptions={[10, 20,40,  50, 100, totalRows]}
  component="div"
  count={totalRows || 0}
  rowsPerPage={Math.round(filters?.pageSize / 1) || 10} // ✅ always matches dropdown
  page={filters?.pageNo || 0}
  onPageChange={onPageChange}
  onRowsPerPageChange={onRowsPerPageChange}
/>
      )}
    </TableWrapper>
  );
};

export default SimManagementTable;